/**
 * One shape for the dashboard's WIP widgets, whatever the data mode.
 *
 * In live mode every figure comes from `GET api/dashboard/wip/summary`: totals
 * over the whole filtered set, one server-sorted and band-filtered page of the
 * aged-WIP table, and the bounded lists the list widgets render. In demo/tour
 * the same shape is derived from the mock WIP rows the context already holds,
 * so the widgets themselves are mode-agnostic.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { WIPReport, WipAgeBand, WipAgingBands } from '@/types';
import { sumWipAgingBands, wipAmountInBand } from '@/lib/wip-aging';
import { useDashboardData, DASHBOARD_WIP_PAGE_SIZE } from '@/contexts/DashboardDataContext';

export interface DashboardWipView {
  /** Portfolio WIP including over-billed (negative) projects. */
  totalWip: number;
  /** WIP carried by projects in credit — what the aged-WIP strip totals. */
  positiveWipTotal: number;
  positiveWipCount: number;
  agingBands: WipAgingBands;

  /** Aged-WIP table: the visible rows, paged server-side in live mode. */
  tableRows: WIPReport[];
  tableTotalCount: number;
  page: number;
  pageSize: number;
  pageCount: number;
  /** True when the table is server-paged, i.e. live mode. */
  isPaged: boolean;
  pageLoading: boolean;
  band: WipAgeBand | null;
  setPage: (page: number) => void;
  /** Band clicks re-filter the whole set, not just the visible page. */
  toggleBand: (band: WipAgeBand | null) => void;

  readyToBill: WIPReport[];
  readyToBillCount: number;
  readyToBillAmount: number;
  upcoming: WIPReport[];
  upcomingCount: number;
  upcomingAmount: number;
  overdue: WIPReport[];
  overdueCount: number;
  overdueAmount: number;
  overBilled: WIPReport[];
  overBilledCount: number;
  overBilledAmount: number;
  underBilled: WIPReport[];
  underBilledCount: number;
  underBilledAmount: number;
  writtenOffCount: number;
  writtenOffAmount: number;
  /** Projects whose oldest WIP is older than the aged-WIP threshold. */
  agedOverThresholdCount: number;
}

export interface DashboardWipViewOptions {
  /** Rows below this under-billing amount are noise and are not listed. */
  underBillingThreshold: number;
  agedWipDaysThreshold: number;
  /** Restricts the demo/tour derivation to a subset, for PM-scoped widgets. */
  filter?: (row: WIPReport) => boolean;
}

export function useDashboardWipView(options: DashboardWipViewOptions): DashboardWipView {
  const { underBillingThreshold, agedWipDaysThreshold, filter } = options;
  const { wipSummary, wipReports, loadWipPage, wipPageLoading } = useDashboardData();

  const [page, setPageState] = useState(1);
  const [band, setBand] = useState<WipAgeBand | null>(null);

  const setPage = useCallback((next: number) => {
    setPageState(next);
    void loadWipPage(next, band, { underBillingThreshold, agedWipDaysThreshold });
  }, [band, loadWipPage, underBillingThreshold, agedWipDaysThreshold]);

  const toggleBand = useCallback((next: WipAgeBand | null) => {
    const resolved = next === band ? null : next;
    setBand(resolved);
    setPageState(1);
    void loadWipPage(1, resolved, { underBillingThreshold, agedWipDaysThreshold });
  }, [band, loadWipPage, underBillingThreshold, agedWipDaysThreshold]);

  // The aged-WIP days threshold is a company billing setting that loads after the
  // dashboard's first fetch, so re-ask once the server's answer was computed
  // against a different threshold than the one now in force.
  const requestedThresholdsRef = useRef<string | null>(null);
  useEffect(() => {
    if (!wipSummary) return;
    const s = wipSummary.summary;
    if (s.agedWipDaysThreshold === agedWipDaysThreshold
      && s.underBillingThreshold === underBillingThreshold) return;

    const key = `${agedWipDaysThreshold}:${underBillingThreshold}`;
    if (requestedThresholdsRef.current === key) return;
    requestedThresholdsRef.current = key;
    void loadWipPage(page, band, { underBillingThreshold, agedWipDaysThreshold });
  }, [wipSummary, agedWipDaysThreshold, underBillingThreshold, page, band, loadWipPage]);

  const rows = useMemo(
    () => (filter ? wipReports.filter(filter) : wipReports),
    [wipReports, filter],
  );

  return useMemo(() => {
    if (wipSummary) {
      const s = wipSummary.summary;
      return {
        totalWip: s.totalWip,
        positiveWipTotal: s.positiveWipTotal,
        positiveWipCount: s.positiveWipCount,
        agingBands: s.agingBands,
        tableRows: wipSummary.rows,
        tableTotalCount: wipSummary.totalCount,
        page,
        pageSize: wipSummary.pageSize,
        pageCount: Math.max(1, Math.ceil(wipSummary.totalCount / wipSummary.pageSize)),
        isPaged: true,
        pageLoading: wipPageLoading,
        band,
        setPage,
        toggleBand,
        readyToBill: wipSummary.readyToBill,
        readyToBillCount: s.readyToBillCount,
        readyToBillAmount: s.readyToBillAmount,
        upcoming: wipSummary.upcoming,
        upcomingCount: s.upcomingCount,
        upcomingAmount: s.upcomingAmount,
        overdue: wipSummary.overdue,
        overdueCount: s.overdueCount,
        overdueAmount: s.overdueAmount,
        overBilled: wipSummary.overBilled,
        overBilledCount: s.overBilledCount,
        overBilledAmount: s.overBilledAmount,
        underBilled: wipSummary.underBilled,
        underBilledCount: s.underBilledCount,
        underBilledAmount: s.underBilledAmount,
        writtenOffCount: s.writtenOffCount,
        writtenOffAmount: s.writtenOffAmount,
        agedOverThresholdCount: s.agedOverThresholdCount,
      };
    }

    // Demo/tour: the mock rows are already in memory, so derive the same figures
    // over the whole set — unpaged and unbounded, exactly as before.
    const nowMs = Date.now();
    const horizonMs = nowMs + 30 * 24 * 60 * 60 * 1000;
    const dueMs = (row: WIPReport) => new Date(row.nextBillingEvent!).getTime();

    const positive = rows.filter(r => r.wipAmount > 0);
    const tableRows = band
      ? rows.filter(r => wipAmountInBand(r, band) !== 0)
      : rows.filter(r => r.wipAmount !== 0);

    const readyToBill = rows.filter(r => r.readyToBill);
    const upcoming = rows
      .filter(r => r.nextBillingEvent && dueMs(r) > nowMs && dueMs(r) <= horizonMs)
      .sort((a, b) => dueMs(a) - dueMs(b));
    const overdue = rows
      .filter(r => r.nextBillingEvent && dueMs(r) < nowMs)
      .sort((a, b) => dueMs(a) - dueMs(b));
    const overBilled = rows
      .filter(r => (r.overBilling ?? 0) > 0)
      .sort((a, b) => (b.overBilling ?? 0) - (a.overBilling ?? 0));
    const underBilled = rows
      .filter(r => (r.underBilling ?? 0) > underBillingThreshold)
      .sort((a, b) => (b.underBilling ?? 0) - (a.underBilling ?? 0));
    const writtenOff = rows.filter(r => (r.writtenOffAmount ?? 0) > 0);
    const sum = (list: WIPReport[], pick: (row: WIPReport) => number) =>
      list.reduce((total, row) => total + pick(row), 0);

    return {
      totalWip: sum(rows, r => r.wipAmount ?? 0),
      positiveWipTotal: sum(positive, r => r.wipAmount),
      positiveWipCount: positive.length,
      agingBands: sumWipAgingBands(positive),
      tableRows,
      tableTotalCount: tableRows.length,
      page: 1,
      pageSize: DASHBOARD_WIP_PAGE_SIZE,
      pageCount: 1,
      isPaged: false,
      pageLoading: false,
      band,
      setPage,
      toggleBand,
      readyToBill,
      readyToBillCount: readyToBill.length,
      readyToBillAmount: sum(readyToBill, r => r.nextBillingEventAmount ?? 0),
      upcoming,
      upcomingCount: upcoming.length,
      upcomingAmount: sum(upcoming, r => r.nextBillingEventAmount ?? 0),
      overdue,
      overdueCount: overdue.length,
      overdueAmount: sum(overdue, r => r.nextBillingEventAmount ?? 0),
      overBilled,
      overBilledCount: overBilled.length,
      overBilledAmount: sum(overBilled, r => r.overBilling ?? 0),
      underBilled,
      underBilledCount: underBilled.length,
      underBilledAmount: sum(underBilled, r => r.underBilling ?? 0),
      writtenOffCount: writtenOff.length,
      writtenOffAmount: sum(writtenOff, r => r.writtenOffAmount ?? 0),
      agedOverThresholdCount: rows.filter(r => (r.agingDays ?? 0) > agedWipDaysThreshold).length,
    };
  }, [
    wipSummary, rows, band, page, wipPageLoading, setPage, toggleBand,
    underBillingThreshold, agedWipDaysThreshold,
  ]);
}
